import { Box } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { ThemeButton } from '../theme-button'

/**
 * @typedef {object} ButtonsToEditProps
 * @property {boolean} [editMode=false] - Si es true, muestra los botones de Cancelar y Guardar. Si es false, muestra el botón de Editar.
 * @property {boolean} [isLoading=false] - Si es true, muestra un indicador de carga en el botón de Guardar y deshabilita el botón de Cancelar.
 * @property {function(): void} handleEdit - Callback que se ejecuta al hacer clic en el botón Editar.
 * @property {function(): void} handleCancel - Callback que se ejecuta al hacer clic en el botón Cancelar.
 * @property {function(): void} handleSave - Callback que se ejecuta al hacer clic en el botón Guardar.
 */

/**
 * Un componente que alterna entre botones de edición y botones de guardar/cancelar.
 *
 * Proporciona una interfaz estándar para manejar el flujo de edición de formularios o datos.
 * En modo no-edición muestra un botón "Editar", mientras que en modo edición muestra
 * botones "Cancelar" y "Guardar" con soporte para indicador de carga durante operaciones asíncronas.
 *
 * @component
 * @example
 * const MyEditableComponent = () => {
 *   const [editMode, setEditMode] = useState(false);
 *   const [loading, setLoading] = useState(false);
 *
 *   const handleEdit = () => {
 *     setEditMode(true);
 *   };
 *
 *   const handleCancel = () => {
 *     setEditMode(false);
 *   };
 *
 *   const handleSave = async () => {
 *     setLoading(true);
 *     await saveData();
 *     setLoading(false);
 *     setEditMode(false);
 *   };
 *
 *   return (
 *     <ButtonsToEdit
 *       editMode={editMode}
 *       isLoading={loading}
 *       handleEdit={handleEdit}
 *       handleCancel={handleCancel}
 *       handleSave={handleSave}
 *     />
 *   );
 * };
 *
 * @param {ButtonsToEditProps} props - Las props del componente ButtonsToEdit.
 */
export const ButtonsToEdit = ({
  editMode = false,
  isLoading = false,
  handleEdit,
  handleCancel,
  handleSave,
}) => {
  const intl = useIntl()

  if (editMode)
    return (
      <Box display="flex" justifyContent="center" m={'14px 0'}>
        <ThemeButton
          size="small"
          variant="outlined"
          onClick={handleCancel}
          disabled={isLoading}
          style={{ marginRight: 20 }}
        >
          {intl.formatMessage({ id: 'global.cancel' })}
        </ThemeButton>
        <ThemeButton
          size="small"
          onClick={handleSave}
          loading={isLoading}
          style={{ marginLeft: 20 }}
        >
          {intl.formatMessage({ id: 'global.save' })}
        </ThemeButton>
      </Box>
    )

  return (
    <Box display="flex" justifyContent="center" m={'14px 0'}>
      <ThemeButton size="small" onClick={handleEdit}>
        {intl.formatMessage({ id: 'global.edit' })}
      </ThemeButton>
    </Box>
  )
}

export default ButtonsToEdit
